<template>
  <div v-if="isImpersonating" class="impersonation-banner">
    <div class="container-fluid">
      <div class="d-flex align-items-center justify-content-between py-2">
        <!-- Left Side: Info -->
        <div class="d-flex align-items-center gap-3">
          <div class="impersonation-icon">
            <i class="bi bi-person-badge"></i>
          </div>
          <div class="impersonation-info">
            <div class="impersonation-text">
              <strong>Impersonating:</strong>
              <span class="ms-2">
                {{ impersonationState.targetUser?.first_name }} {{ impersonationState.targetUser?.last_name }}
              </span>
              <span class="text-muted ms-2">({{ impersonationState.targetUser?.email }})</span>
            </div>
            <div class="impersonation-meta">
              <small class="text-muted">
                As admin: {{ impersonationState.originalUser?.name }} |
                Duration: {{ duration }}
              </small>
            </div>
          </div>
        </div>

        <!-- Right Side: Exit Button -->
        <button
          class="btn btn-sm btn-light"
          @click="handleStopImpersonation"
          :disabled="stopping"
        >
          <i class="bi bi-x-circle me-1"></i>
          {{ stopping ? 'Exiting...' : 'Exit Impersonation' }}
        </button>
      </div>
    </div>
  </div>

  <!-- Confirmation Modal -->
  <ConfirmModal ref="confirmModal" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useImpersonation } from '@/composables/useImpersonation';
import { useAuthStore } from '@/stores/auth';
import ConfirmModal from '@/components/shared/ConfirmModal.vue';

const authStore = useAuthStore();

const {
  isImpersonating,
  impersonationState,
  stopImpersonation,
  getStatus,
  getImpersonationDuration,
  mightBeImpersonating
} = useImpersonation();

const stopping = ref(false);
const duration = ref('');
const confirmModal = ref(null);

let durationInterval = null;

// Update duration every minute
function updateDuration() {
  duration.value = getImpersonationDuration();
}

// Handle stop impersonation
async function handleStopImpersonation() {
  const confirmed = await confirmModal.value.showModal({
    title: 'Exit Impersonation',
    message: 'Are you sure you want to exit impersonation mode?',
    details: `You will return to your admin session as ${impersonationState.value.originalUser?.name || 'admin'}.`,
    confirmButtonText: 'Exit Impersonation',
    confirmButtonClass: 'btn-warning'
  });

  if (confirmed) {
    stopping.value = true;
    await stopImpersonation();
    // stopImpersonation will reload the page, so we don't need to set stopping back to false
  }
}

// Check impersonation status on mount
onMounted(async () => {
  // Check impersonation status if:
  // 1. Current user is admin (can impersonate others), OR
  // 2. There might be an active impersonation session (from localStorage)
  //    This handles the case where an admin impersonates a non-admin user
  if (authStore.isAdmin || mightBeImpersonating.value) {
    await getStatus();
    updateDuration();

    // Update duration every minute
    durationInterval = setInterval(updateDuration, 60000);
  }
});

// Clean up interval on unmount
onUnmounted(() => {
  if (durationInterval) {
    clearInterval(durationInterval);
  }
});
</script>

<style scoped>
.impersonation-banner {
  background: linear-gradient(135deg, #7B8FD4 0%, #5a6eb8 100%);
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 1040;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.impersonation-icon {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.impersonation-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.impersonation-text {
  font-size: 0.95rem;
}

.impersonation-meta {
  font-size: 0.8rem;
  opacity: 0.9;
}

.text-muted {
  color: rgba(255, 255, 255, 0.7) !important;
}

.btn-light {
  background-color: rgba(255, 255, 255, 0.9);
  border: none;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-light:hover:not(:disabled) {
  background-color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-light:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .impersonation-info {
    font-size: 0.85rem;
  }

  .impersonation-meta {
    font-size: 0.75rem;
  }

  .btn-light {
    font-size: 0.85rem;
    padding: 0.25rem 0.5rem;
  }
}
</style>
